import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRillContext } from "../../../../../rill/RillReact/share/base";
import type { Athlete } from "../../../../../types/user";
import {
  readFilterSectionBlocks,
  readFilterSummaries,
  rememberConfigListFilter,
  rememberFilterSummaries,
  rememberLoadedConfigs,
  rememberSectionBlocks,
} from "../../../../../utils/configs-by-client-id-cache";
import type { ClientConfigWidgetAvailability } from "../../../../athlete-portal/profile/components/ClientConfiguration/utils";
import {
  ConfigFilterChips,
  ConfigFilterChipsSkeleton,
} from "./ConfigFilterChips";
import { ConfigSearchInput } from "./ConfigSearchInput";
import { ConfigsTable } from "./ConfigsTable";
import {
  type ConfigListFilterCounts,
  type ConfigListFilters,
  countActiveFilters,
  EMPTY_FILTERS,
  FILTERS_QUERY_PARAM,
  filterScopeKey,
  matchFiltersLocally,
  parseFilters,
  readCachedFilterResult,
  rememberFilterResult,
  serializeFilters,
  syncFiltersToUrl,
  toApiFilters,
} from "./helpers/configFilters";

// Matches msb-api client-configurations/list POST MAX_BATCH_SIZE.
const MAX_CONFIGS_BATCH_SIZE = 50;

/**
 * Tabular view of client-configurations/list payloads keyed by client id.
 * For a cold, large list the server only sends the first page (see
 * `configsPagination` / athletes.controller.js). Remaining configs are
 * fetched for whichever unloaded rows are actually on screen, so a fast
 * jump down a 400-client table loads the landed window instead of
 * every page from the top — and can't freeze by skipping a sentinel.
 *
 * Coach filters (chips) are matched by the API over the whole list —
 * `filterableAthletes`, before the name search — so they don't depend on
 * which configs have loaded; the table then shows the matching rows.
 */
const AthletesConfigList = ({
  referAsClients,
  filterMode,
  activeAthletes = [],
  filterableAthletes = activeAthletes,
  clientConfigWidgetAvailability,
  search = "",
  onSearchChange = () => {},
}: {
  referAsClients: boolean;
  filterMode: string | boolean;
  /** active athletes after the name search — what the table shows */
  activeAthletes?: Athlete[];
  /** active athletes before the name search — what filters and counts cover */
  filterableAthletes?: Athlete[];
  clientConfigWidgetAvailability?: ClientConfigWidgetAvailability;
  /** name search text */
  search?: string;
  onSearchChange?: (value: string) => void;
}) => {
  const { t } = useTranslation(["coaching-portal"]);
  const { locals } = useRillContext();
  const {
    configsByClientId,
    configsPagination,
    sectionBlocksByClientId,
    prefetchClientIds,
    isSectionBlocksCached,
    query,
  } = locals;
  const [loadedConfigs, setLoadedConfigs] = useState(configsByClientId);

  // Has-data flags: the ones already known from earlier views (shared map)
  // show on the first frame; the rest come from whichever answers first —
  // a has-data request for just those clients, or the filter response
  // (which computes them anyway).
  const sectionBlockIds = useMemo(
    () =>
      prefetchClientIds && !isSectionBlocksCached
        ? String(prefetchClientIds).split(",").filter(Boolean)
        : [],
    [prefetchClientIds, isSectionBlocksCached]
  );
  const [initialSectionBlocks] = useState(() => {
    const known = readFilterSectionBlocks();
    return {
      ...sectionBlocksByClientId,
      ...Object.fromEntries(
        sectionBlockIds.filter((id) => known[id]).map((id) => [id, known[id]])
      ),
    };
  });
  const [blockedSections, setBlockedSections] = useState(initialSectionBlocks);
  const sectionBlocksRef = useRef(initialSectionBlocks);
  const missingSectionBlockIds = () =>
    sectionBlockIds.filter((id) => !sectionBlocksRef.current?.[id]);

  const receiveSectionBlocks = useCallback(
    (flags: typeof sectionBlocksByClientId | undefined) => {
      if (flags && Object.keys(flags).length) {
        sectionBlocksRef.current = { ...sectionBlocksRef.current, ...flags };
        setBlockedSections(sectionBlocksRef.current);
        rememberFilterSummaries(undefined, flags);
      }
      // Only once complete: the controller treats this view's key as fully
      // cached on the next visit.
      if (
        sectionBlockIds.length &&
        sectionBlockIds.every((id) => sectionBlocksRef.current?.[id])
      ) {
        rememberSectionBlocks(
          Object.fromEntries(
            sectionBlockIds.map((id) => [id, sectionBlocksRef.current[id]])
          ),
          prefetchClientIds
        );
      }
    },
    [sectionBlockIds, prefetchClientIds]
  );

  const sectionBlocksRequestedRef = useRef(false);
  const fetchSectionBlocks = useCallback(async () => {
    const ids = sectionBlockIds.filter((id) => !sectionBlocksRef.current?.[id]);
    if (!ids.length || sectionBlocksRequestedRef.current) return;
    sectionBlocksRequestedRef.current = true;
    try {
      const response = await fetch(
        "/actions/client-configuration/prefetch-section-blocks",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ clientIds: ids.join(",") }),
        }
      );
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      receiveSectionBlocks(data.sectionBlocksByClientId);
    } catch {
      // The filter response may still bring them.
    }
  }, [sectionBlockIds, receiveSectionBlocks]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: once per mount (the list remounts per view)
  useEffect(() => {
    if (missingSectionBlockIds().length) fetchSectionBlocks();
    else receiveSectionBlocks(undefined);
  }, []);

  const loadedConfigsRef = useRef(loadedConfigs);
  loadedConfigsRef.current = loadedConfigs;
  const queuedIdsRef = useRef(new Set<string>());
  const inFlightIdsRef = useRef(new Set<string>());
  const skippedIdsRef = useRef(new Set<string>());
  const isDrainingRef = useRef(false);

  const drainVisibleConfigsQueue = useCallback(async () => {
    if (!configsPagination || isDrainingRef.current) return;
    isDrainingRef.current = true;

    const takeBatch = () => {
      const batch: string[] = [];
      for (const id of queuedIdsRef.current) {
        queuedIdsRef.current.delete(id);
        if (
          loadedConfigsRef.current?.[id] ||
          inFlightIdsRef.current.has(id) ||
          skippedIdsRef.current.has(id)
        ) {
          continue;
        }
        inFlightIdsRef.current.add(id);
        batch.push(id);
        if (batch.length >= MAX_CONFIGS_BATCH_SIZE) break;
      }
      return batch;
    };

    try {
      while (queuedIdsRef.current.size) {
        const batch = takeBatch();
        if (!batch.length) break;

        const response = await fetch(
          "/actions/client-configuration/fetch-configs-batch",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              clientIds: batch,
              filterGroup: configsPagination.filterGroup,
              organizationCoach: configsPagination.organizationCoach,
            }),
          }
        );
        if (!response.ok) {
          for (const id of batch) inFlightIdsRef.current.delete(id);
          break;
        }
        const data = await response.json();
        const returned = data.configsByClientId ?? {};
        setLoadedConfigs((prev) => ({ ...prev, ...returned }));
        rememberLoadedConfigs(returned);
        for (const id of batch) {
          inFlightIdsRef.current.delete(id);
          if (!returned[id]) skippedIdsRef.current.add(id);
        }
      }
    } catch {
      for (const id of [...inFlightIdsRef.current]) {
        inFlightIdsRef.current.delete(id);
      }
    } finally {
      isDrainingRef.current = false;
    }
    if (queuedIdsRef.current.size) drainVisibleConfigsQueue();
  }, [configsPagination]);

  const loadVisibleConfigs = useCallback(
    (ids: string[]) => {
      if (!configsPagination) return;
      let queued = false;
      for (const id of ids) {
        if (
          !id ||
          loadedConfigsRef.current?.[id] ||
          inFlightIdsRef.current.has(id) ||
          skippedIdsRef.current.has(id) ||
          queuedIdsRef.current.has(id)
        ) {
          continue;
        }
        queuedIdsRef.current.add(id);
        queued = true;
      }
      if (queued) drainVisibleConfigsQueue();
    },
    [configsPagination, drainVisibleConfigsQueue]
  );

  // --- Coach filters -------------------------------------------------------
  // `filters` is what the table reflects (and what the URL holds); the chips
  // edit `draftFilters` until the coach presses Apply.
  const [filters, setFilters] = useState<ConfigListFilters>(() =>
    parseFilters(query?.[FILTERS_QUERY_PARAM])
  );
  const [draftFilters, setDraftFilters] = useState(filters);
  const [isFiltering, setIsFiltering] = useState(false);
  const [filterError, setFilterError] = useState(false);
  const latestRequestRef = useRef(0);

  const applyFilters = (next: ConfigListFilters) => {
    setFilters(next);
    setDraftFilters(next);
    syncFiltersToUrl(next);
  };

  const activeFilterCount = countActiveFilters(filters);
  const filterKey = serializeFilters(filters) ?? "";
  const filterableIds = useMemo(
    () =>
      filterableAthletes
        .map((athlete) => (athlete?._id ? String(athlete._id) : ""))
        .filter(Boolean),
    [filterableAthletes]
  );
  const filterableIdsKey = filterableIds.join(",");
  const scopeKey = filterScopeKey(
    query?.filterGroup || undefined,
    query?.organizationCoach || undefined,
    filterableIdsKey
  );

  // Matched locally when every client here already has a filter summary
  // (e.g. "All clients" was filtered before switching to a group) — no
  // request then. Otherwise the last response for this scope, if any, so a
  // switch back renders right away while the effect below re-requests.
  const [initialResult] = useState(() => {
    const local = matchFiltersLocally(
      readFilterSummaries(),
      filterableIds,
      filters
    );
    if (local) return { ...local, filterKey };
    const cached = readCachedFilterResult(scopeKey, filterKey);
    return cached
      ? { ...cached, filterKey: cached.matchingIds ? filterKey : null }
      : null;
  });
  const [counts, setCounts] = useState<ConfigListFilterCounts | null>(
    initialResult?.counts ?? null
  );
  // null until a result for the current filters arrives
  const [matchingIds, setMatchingIds] = useState<Set<string> | null>(
    initialResult?.matchingIds ?? null
  );
  // Which filters `matchingIds` answers — when it's the current ones, a
  // request in flight is only a background refresh, so don't show it as
  // filtering.
  const [resultFilterKey, setResultFilterKey] = useState<string | null>(
    initialResult?.filterKey ?? null
  );

  // Also runs with no filters on: that response carries the chip counts.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the serialized filters / ids so a new object with the same contents doesn't refetch
  useEffect(() => {
    if (!filterableIds.length) return;
    const requestId = ++latestRequestRef.current;
    const local = matchFiltersLocally(
      readFilterSummaries(),
      filterableIds,
      filters
    );
    if (local) {
      setCounts(local.counts);
      setMatchingIds(local.matchingIds);
      setResultFilterKey(filterKey);
      setIsFiltering(false);
      setFilterError(false);
      rememberFilterResult(
        scopeKey,
        filterKey,
        local.counts,
        local.matchingIds
      );
      return;
    }
    const cached = readCachedFilterResult(scopeKey, filterKey);
    if (cached) {
      setCounts(cached.counts);
      if (cached.matchingIds) {
        setMatchingIds(cached.matchingIds);
        setResultFilterKey(filterKey);
      }
    }
    setIsFiltering(true);
    setFilterError(false);
    const cacheArgs = {
      clientIds: filterableIds,
      filterGroup: query?.filterGroup || undefined,
      organizationCoach: query?.organizationCoach || undefined,
      filters: toApiFilters(filters),
      match: filters.match,
    };
    const requestFilteredIds = async () => {
      try {
        const response = await fetch("/actions/client-configuration/filter", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clientIds: cacheArgs.clientIds,
            filterGroup: cacheArgs.filterGroup,
            organizationCoach: cacheArgs.organizationCoach,
            // Sent as a string so the GUI server's body casting leaves
            // values like "on" / "off" alone (see actions/client-config.js).
            filtersJson: JSON.stringify(cacheArgs.filters),
            // Has-data flags the table already has, so the API doesn't
            // re-check them (see actions/client-config.js).
            sectionBlocksJson: JSON.stringify(
              Object.fromEntries(
                filterableIds
                  .filter((id) => blockedSections?.[id])
                  .map((id) => [id, blockedSections[id]])
              )
            ),
            match: cacheArgs.match,
          }),
        });
        if (requestId !== latestRequestRef.current) return;
        if (!response.ok) throw new Error(String(response.status));
        const data = await response.json();
        rememberConfigListFilter(cacheArgs, data);
        rememberFilterSummaries(
          data.summariesByClientId,
          data.sectionBlocksByClientId
        );
        if (missingSectionBlockIds().length) {
          receiveSectionBlocks(data.sectionBlocksByClientId);
        }
        if (requestId !== latestRequestRef.current) return;
        const nextMatchingIds = new Set<string>(data.matchingIds);
        setCounts(data.counts);
        setMatchingIds(nextMatchingIds);
        setResultFilterKey(filterKey);
        rememberFilterResult(scopeKey, filterKey, data.counts, nextMatchingIds);
      } catch {
        if (requestId === latestRequestRef.current) setFilterError(true);
      } finally {
        if (requestId === latestRequestRef.current) setIsFiltering(false);
      }
    };
    requestFilteredIds();
  }, [filterKey, scopeKey]);

  // Keep showing the previous results while a new request is in flight.
  const shownAthletes = activeFilterCount
    ? matchingIds
      ? activeAthletes.filter((athlete) => matchingIds.has(String(athlete._id)))
      : []
    : activeAthletes;
  const filtersPending = activeFilterCount > 0 && !matchingIds;
  const isFilteringShown = isFiltering && resultFilterKey !== filterKey;
  // No results yet for this view (e.g. right after a group switch): show its
  // rows under the filtering overlay rather than no table at all.
  const showPendingRows = filtersPending && !filterError;
  const tableAthletes = showPendingRows ? activeAthletes : shownAthletes;
  // Also from the first frame, before the request has started.
  const showFilteringOverlay =
    (isFilteringShown && activeFilterCount > 0) || showPendingRows;

  // Not while pending: those rows may be about to be filtered out.
  const hasUnloadedVisibleCandidates =
    Boolean(configsPagination) &&
    !filtersPending &&
    shownAthletes.some((athlete) => !loadedConfigs?.[String(athlete._id)]);

  const filterModeShowsActive = ["all", false].includes(filterMode);
  const showActive = filterModeShowsActive && tableAthletes.length;
  const showNoFilterMatches =
    filterModeShowsActive &&
    activeFilterCount > 0 &&
    !filtersPending &&
    !shownAthletes.length &&
    activeAthletes.length > 0;
  const showEmptySearch =
    filterModeShowsActive && !activeAthletes.length && !filtersPending;

  const searchInput = (
    <ConfigSearchInput
      referAsClients={referAsClients}
      value={search}
      onChange={onSearchChange}
    />
  );
  const showFilterChips = filterModeShowsActive && filterableIds.length > 0;

  return (
    <div className="athletes-config-list">
      {!showFilterChips && (
        <div className="athletes-config-list__filters">
          <div className="athletes-config-list__filters-chips">
            {searchInput}
          </div>
        </div>
      )}
      {showFilterChips && !counts && !filterError && (
        <ConfigFilterChipsSkeleton search={searchInput} />
      )}
      {showFilterChips && (counts || filterError) && (
        <ConfigFilterChips
          search={searchInput}
          referAsClients={referAsClients}
          filters={draftFilters}
          appliedFilters={filters}
          counts={counts}
          shownCount={matchingIds ? shownAthletes.length : null}
          isFiltering={isFilteringShown}
          onChange={setDraftFilters}
          onApply={() => applyFilters(draftFilters)}
          onClear={() => applyFilters(EMPTY_FILTERS)}
        />
      )}
      {filterError && (
        <p className="athletes-config-list__filters-error">
          {t("athletes.configFilters.loadError")}
        </p>
      )}
      {showActive ? (
        <section
          className="athletes-config-list__section"
          data-filtering={showFilteringOverlay ? true : undefined}
        >
          <ConfigsTable
            athletes={tableAthletes}
            configsByClientId={loadedConfigs}
            referAsClients={referAsClients}
            clientConfigWidgetAvailability={clientConfigWidgetAvailability}
            blockedSections={blockedSections}
            onVisibleUnloadedIds={
              hasUnloadedVisibleCandidates ? loadVisibleConfigs : undefined
            }
          />
          {showFilteringOverlay && (
            <div className="athletes-config-list__filtering-overlay">
              <output
                className="athletes-config-list__filtering-spinner"
                aria-label={t("athletes.configFilters.filtering")}
              >
                <span className="athletes-config-list__spinner-ring" />
              </output>
            </div>
          )}
        </section>
      ) : null}
      {showNoFilterMatches && (
        <div className="athletes-config-list__empty-search">
          <p className="athletes-config-list__empty-search-text">
            {t("athletes.configFilters.noMatches", {
              role: referAsClients
                ? t("athletes.roleClientsLower")
                : t("athletes.roleAthletesLower"),
            })}
          </p>
          <button
            type="button"
            data-button-type="secondary"
            onClick={() => applyFilters(EMPTY_FILTERS)}
          >
            {t("athletes.configFilters.clearAll")}
          </button>
        </div>
      )}
      {showEmptySearch && (
        <div className="athletes-config-list__empty-search">
          <p className="athletes-config-list__empty-search-text">
            {t("athleteSelect.noResultsFoundLong")}
          </p>
        </div>
      )}
    </div>
  );
};

export default AthletesConfigList;
